import { useParams, Link, Navigate } from 'react-router-dom'
import { SEO } from '@/components/seo'
import { SignupCta } from '@/components/ui'
import { getBlogPostBySlug } from '@/data/blog/posts'

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>()
  const post = slug ? getBlogPostBySlug(slug) : undefined

  if (!post) {
    return <Navigate to="/blog" replace />
  }

  return (
    <>
      <SEO
        title={`${post.title} | WealthSpace`}
        description={post.excerpt}
        url={`/blog/${post.slug}`}
      />

      {/* Hero Banner */}
      <div
        className="py-20 px-6 relative overflow-hidden"
        style={{ background: post.gradient }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.1)_0%,transparent_50%)]" />
        <div className="max-w-[800px] mx-auto text-center relative z-10">
          <div className="text-sm font-semibold uppercase tracking-widest text-white/80 mb-4">
            {post.category}
          </div>
          <h1 className="font-serif text-[clamp(28px,5vw,48px)] font-normal text-white leading-tight mb-6">
            {post.title}
          </h1>
          <div className="text-white/70 text-[15px]">
            {post.date} · {post.readTime}
          </div>
        </div>
      </div>

      {/* Article Content */}
      <article className="max-w-[720px] mx-auto px-6 py-16">
        <Link
          to="/resources"
          className="inline-flex items-center gap-2 text-blue-400 font-medium mb-8 hover:gap-3 transition-all"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="w-4 h-4"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18"
            />
          </svg>
          Back to Resources
        </Link>

        <div
          className="prose prose-invert max-w-none
            [&_h2]:font-serif [&_h2]:text-[28px] [&_h2]:font-medium [&_h2]:text-brand-light [&_h2]:mt-12 [&_h2]:mb-5
            [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-brand-light [&_h3]:mt-8 [&_h3]:mb-4
            [&_p]:text-[17px] [&_p]:text-brand-muted [&_p]:leading-relaxed [&_p]:mb-5
            [&_ul]:text-[17px] [&_ul]:text-brand-muted [&_ul]:my-5 [&_ul]:ml-6 [&_ul]:list-disc
            [&_li]:mb-3
            [&_strong]:text-brand-light
            [&_blockquote]:border-l-4 [&_blockquote]:border-blue-400 [&_blockquote]:pl-6 [&_blockquote]:my-8 [&_blockquote]:italic [&_blockquote]:text-blue-300"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </article>

      {/* CTA Section */}
      <section className="py-16 px-6 text-center bg-slate-50">
        <h2 className="font-serif text-[32px] font-normal text-brand-light mb-4">
          Ready to see WealthSpace in action?
        </h2>
        <p className="text-lg text-brand-muted mb-8">
          Sign up free and discover how we can help transform your practice.
        </p>
        <SignupCta location="blog_post_cta">
          Start Free Trial
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="w-[18px] h-[18px]"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
            />
          </svg>
        </SignupCta>
      </section>
    </>
  )
}
